/** @format */

import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag,
  Calendar,
  CheckCircle,
  Package,
  AlertCircle,
  Hash,
  DollarSign,
  Info,
  ArrowRight,
  ListOrdered,
  Ruler,
} from "lucide-react"; // Added Ruler
import moment from "moment";

// Actions & Components (Ensure paths are correct)
import { getOrderDetails } from "../actions/orderActions"; // Assuming this fetches LIST of orders
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Toast } from "./customize/Toast"; // Assuming path
import { Helmet } from "react-helmet";

// --- Helper Function (Using original logic) ---

// --- Reusable Order Card Component (Adapting to original data structure) ---
const OrderCard = ({ order, index }) => {
  const {
    // Assuming these are top-level properties based on original table
    orderId = null, // If there isn't a dedicated ID, use index or find unique key
    date,
    status = "Processing", // Add status if available, else default
    quantity,
    shade, // Product type/name
    group, // Fabric group/transparency?
    width,
    length,
    controlSystem,
    valance,
    hem,
    measure, // Mount type
    // Need total price if available per order line, otherwise omit or calculate differently
    // itemPrice, // Assuming this might exist or needs calculation
    // orderTotal // Maybe available at top level of order object?
  } = order || {};

  // --- Formatting ---
  const formattedDate = date ? moment(date).format("MMM D, YYYY") : "N/A";
  const dimensions =
    width && length
      ? `${parseFloat(width).toFixed(3)}" x ${parseFloat(length).toFixed(3)}"`
      : "N/A";
  // const itemTotalDisplay = itemPrice ? `$${parseFloat(itemPrice).toFixed(2)}` : 'N/A'; // Adjust currency

  return (
    <motion.div
      key={orderId || index} // Use orderId if available
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
    >
      {/* Card Header - Simplified Info */}
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-1.5 text-gray-700">
          <Calendar size={14} />
          <span className="font-medium">{formattedDate}</span>
        </div>
        {orderId && ( // Show Order ID if available
          <div className="flex items-center gap-1.5 text-gray-700">
            <Hash size={14} />
            {/* Link to details page */}
            <Link
              to={`/order/${orderId}`}
              className="font-medium hover:text-primary hover:underline"
            >
              #{orderId}
            </Link>
          </div>
        )}
        {/* <div className="flex items-center gap-1.5">
					<span
						className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
							status.toLowerCase() === "shipped"
								? "bg-blue-100 text-blue-700"
								: status.toLowerCase() === "delivered"
								? "bg-green-100 text-green-700"
								: "bg-yellow-100 text-yellow-700"
						}`}
					>
						{status}
					</span>
				</div> */}
        {/* Add Total Price if available at this level */}
        {/* <div className="font-semibold text-primary">{orderTotalDisplay}</div> */}
      </div>

      {/* Card Body - Main Item Details from the Row */}
      <div className="p-4 grid grid-cols-[auto,1fr] sm:grid-cols-[auto,1fr,auto] gap-x-4 gap-y-2 text-sm">
        {/* Left Icon/Type */}
        <div className="row-span-4 pr-4 sm:border-r border-gray-100 flex flex-col items-center justify-center text-center">
          {/* Placeholder Icon */}
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-1">
            <Package size={24} strokeWidth={1.5} />
          </div>
          <span className="text-xs font-medium text-gray-500">
            Qty: {quantity || 1}
          </span>
        </div>

        {/* Middle Details */}
        <div className="col-span-1 space-y-1">
          <p className="font-semibold text-base text-gray-800">
            {shade || "N/A"}
          </p>
          {group && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-500">Group:</span> {group}
            </p>
          )}
          <p className="text-gray-600">
            <span className="font-medium text-gray-500">Size:</span>{" "}
            {dimensions}
          </p>
          {measure && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-500">Mount:</span>{" "}
              {measure}
            </p>
          )}
          {controlSystem && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-500">Control:</span>{" "}
              {controlSystem}
            </p>
          )}
          {valance && valance !== "-" && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-500">Valance:</span>{" "}
              {valance}
            </p>
          )}
          {hem && hem !== "-" && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-500">Hem:</span> {hem}
            </p>
          )}
        </div>

        {/* Right Actions (Desktop) */}
        <div className="col-span-2 sm:col-span-1 sm:row-span-4 flex sm:flex-col items-center sm:items-end justify-start sm:justify-center gap-2 sm:pl-4 sm:border-l border-gray-100">
          {/* Add Track Package button conditionally */}
          {status.toLowerCase() === "shipped" && order.trackingLink && (
            <a
              href={order.trackingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary-outline btn-sm w-full sm:w-auto"
            >
              Track Package
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// --- Main My Orders Screen Component ---
const MyOrderScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- Redux State ---
  const { userInfo } = useSelector((state) => state.userLogin);
  // *** Use the correct state slice where the list of orders is stored by getOrderDetails ***
  const {
    loading,
    error,
    orders = [],
  } = useSelector((state) => state.orderDetails || { orders: [] }); // Assuming it's still orderDetails, adjust if different

  // --- Effects ---
  useEffect(() => {
    if (!userInfo) {
      navigate("/login?redirect=/myorders");
    } else {
      // Dispatch action to fetch the LIST of orders
      dispatch(getOrderDetails()); // Ensure this fetches the LIST
    }
  }, [navigate, dispatch, userInfo]);

  return (
    <div className="min-h-screen bg-gray-100 py-10 md:py-12">
      <Helmet>
        {" "}
        <title>My Orders | Off Cut Shades</title>{" "}
        <meta name="robots" content="noindex,nofollow" />{" "}
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {" "}
            <ShoppingBag className="w-8 h-8 text-primary" />{" "}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              My Orders
            </h1>{" "}
          </div>
          {orders.length > 0 && (
            <p className="text-sm text-gray-600">
              You have {orders.length} order(s).
            </p>
          )}
        </div>

        {/* --- Conditional Rendering --- */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {" "}
              <LoadingSpinner text="Loading your orders..." />{" "}
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              /* ... Error message ... */ className="text-center py-12 text-red-600 bg-red-50 p-6 rounded-lg border border-red-200"
            >
              {" "}
              <AlertCircle
                size={40}
                className="mx-auto text-red-400 mb-3"
              />{" "}
              <p className="font-medium">Could not load orders.</p>{" "}
              <p className="text-sm mt-1">{error}</p>{" "}
              <button
                onClick={() => dispatch(getOrderDetails())}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Try Again
              </button>{" "}
            </motion.div>
          ) : orders.length === 0 ? (
            <motion.div
              key="empty"
              /* ... Empty State ... */ className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12 text-center flex flex-col items-center"
            >
              {" "}
              <Package
                className="w-16 h-16 text-gray-300 mb-5"
                strokeWidth={1}
              />{" "}
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                No Orders Found
              </h2>{" "}
              <p className="text-gray-500 mb-6 max-w-sm">
                You haven't placed any orders yet. Start exploring our custom
                shades!
              </p>{" "}
              <Link
                to="/shop"
                className="btn btn-primary text-base px-6 py-2.5"
              >
                {" "}
                Shop Now{" "}
              </Link>{" "}
            </motion.div>
          ) : (
            // --- Orders List ---
            <motion.div
              key="orders-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4 md:space-y-6"
            >
              {/* Sort orders by date */}
              {orders
                .slice()
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((order, index) => (
                  // Pass the row data directly as 'order' prop
                  <OrderCard
                    key={order.orderId || index}
                    order={order}
                    index={index}
                  />
                ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* No Toast needed here generally, error shown inline */}
    </div>
  );
};

export default MyOrderScreen;
